import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {type ImagePickerAsset} from 'expo-image-picker'
import {AtUri} from '@atproto/api'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'
import {useIsFocused, useNavigation} from '@react-navigation/native'
import {useQueryClient} from '@tanstack/react-query'

import {
  type BackgroundMusicChoice,
  musicChoiceFromRecord,
  musicChoiceToRecord,
} from '#/lib/backgroundMusic'
import {capsuleContentSignature} from '#/lib/capsuleContentSignature'
import {
  type CapsuleDraftRef,
  forgetCapsuleDraft,
  rememberCapsuleDraft,
} from '#/lib/capsuleDrafts'
import {
  type CapsuleRecoveredDraft,
  type CapsuleSnapshot,
  getCapsuleSnapshot,
  offerableCapsuleSnapshot,
} from '#/lib/capsuleRecovery'
import {deleteCapsuleDraft, saveCapsuleDraft} from '#/lib/capsuleSave'
import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
  type NavigationProp,
} from '#/lib/routes/types'
import {createActorCapsuleDraftsQueryKey} from '#/state/queries/capsule'
import {useAgent} from '#/state/session'
import * as Toast from '#/view/com/util/Toast'
import {
  CapsuleCoverStage,
  type CapsuleCoverStageHandle,
} from '#/screens/CapsuleStudio/components/CapsuleCoverStage'
import {AccordionSlideEditor} from '#/screens/CapsuleStudio/components/slides/AccordionSlideEditor'
import {ComplexFillBlankSlideEditor} from '#/screens/CapsuleStudio/components/slides/ComplexFillBlankSlideEditor'
import {ContentSlideEditor} from '#/screens/CapsuleStudio/components/slides/ContentSlideEditor'
import {DragWordsSlideEditor} from '#/screens/CapsuleStudio/components/slides/DragWordsSlideEditor'
import {FillBlankSlideEditor} from '#/screens/CapsuleStudio/components/slides/FillBlankSlideEditor'
import {FillBlanksSlideEditor} from '#/screens/CapsuleStudio/components/slides/FillBlanksSlideEditor'
import {FindWordsSlideEditor} from '#/screens/CapsuleStudio/components/slides/FindWordsSlideEditor'
import {ImageChoiceSlideEditor} from '#/screens/CapsuleStudio/components/slides/ImageChoiceSlideEditor'
import {MarkWordsSlideEditor} from '#/screens/CapsuleStudio/components/slides/MarkWordsSlideEditor'
import {MatchingSlideEditor} from '#/screens/CapsuleStudio/components/slides/MatchingSlideEditor'
import {MultiChoiceSlideEditor} from '#/screens/CapsuleStudio/components/slides/MultiChoiceSlideEditor'
import {OrderingSlideEditor} from '#/screens/CapsuleStudio/components/slides/OrderingSlideEditor'
import {SingleChoiceSlideEditor} from '#/screens/CapsuleStudio/components/slides/SingleChoiceSlideEditor'
import {TrueFalseSlideEditor} from '#/screens/CapsuleStudio/components/slides/TrueFalseSlideEditor'
import {CapsuleThemeSelectorView} from '#/screens/CapsuleThemeSelector'
import {atoms as a} from '#/alf'
import {CapsuleDraftsDialog} from '#/components/capsules/CapsuleDraftsDialog'
import {CapsuleLeaveDialog} from '#/components/capsules/CapsuleLeaveDialog'
import {CapsulePreviewModal} from '#/components/capsules/CapsulePreviewModal'
import {CapsuleRecoveryDialog} from '#/components/capsules/CapsuleRecoveryDialog'
import {PublishCapsuleDialog} from '#/components/capsules/PublishCapsuleDialog'
import {useCapsuleAutosave} from '#/components/capsules/useCapsuleAutosave'
import {useCapsuleDrafts} from '#/components/capsules/useCapsuleDrafts'
import {useDialogControl} from '#/components/Dialog'
import {
  ArrowLeft_Stroke2_Corner0_Rounded as ArrowLeft,
  ArrowRight_Stroke2_Corner0_Rounded as ArrowRight,
} from '#/components/icons/Arrow'
import {PlusLarge_Stroke2_Corner0_Rounded as Plus} from '#/components/icons/Plus'
import {Trash_Stroke2_Corner0_Rounded as TrashIcon} from '#/components/icons/Trash'
import * as Layout from '#/components/Layout'
import {BackgroundMusicSheet} from '#/components/studio/BackgroundMusicSheet'
import {StudioAutosaveNote} from '#/components/studio/StudioAutosaveNote'
import {StudioBlockPicker} from '#/components/studio/StudioBlockPicker'
import {StudioConfirmDialog} from '#/components/studio/StudioConfirmDialog'
import {FirstFieldFocus} from '#/components/studio/StudioFirstFieldFocus'
import {
  type StudioIssue,
  StudioIssuesDialog,
} from '#/components/studio/StudioIssuesDialog'
import {Text} from '#/components/Typography'
import {IS_DEV} from '#/env'
import {listThemes} from '#/themes/registry' // nodenext false-positive: file exists at src/themes/registry.ts
import {readableAccent, type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'
import {type Slide, SLIDE_TYPE_META, type SlideType} from '#/types/capsule'

function SlideEditorOverlay({
  slide,
  themeColor,
  onUpdate,
  onPreview,
}: {
  slide: Slide
  themeColor: string
  onUpdate: (data: Partial<Slide>) => void
  onPreview?: () => void
}) {
  const props = {slide, onChange: onUpdate, primaryColor: themeColor, onPreview}
  switch (slide.type) {
    /*
     * A `video` slide only ever exists as the capsule's cover, and the cover is
     * not a slide editor — it is the studio's first screen, rendered above this
     * switch (see `CapsuleCoverStage`). Reaching here would mean a video block on a
     * content slide, which the picker cannot offer.
     */
    case 'video':
      return null
    case 'content':
      return <ContentSlideEditor {...props} />
    case 'true_false':
      return <TrueFalseSlideEditor {...props} />
    case 'single_choice':
      return <SingleChoiceSlideEditor {...props} />
    case 'multiple_choice':
      return <MultiChoiceSlideEditor {...props} />
    case 'image_choice':
      return <ImageChoiceSlideEditor {...props} />
    case 'matching':
      return <MatchingSlideEditor {...props} />
    case 'ordering':
      return <OrderingSlideEditor {...props} />
    case 'fill_blank':
      return <FillBlankSlideEditor {...props} />
    case 'fill_blanks':
      return <FillBlanksSlideEditor {...props} />
    case 'accordion':
      return <AccordionSlideEditor {...props} />
    case 'drag_words':
      return <DragWordsSlideEditor {...props} />
    case 'mark_words':
      return <MarkWordsSlideEditor {...props} />
    case 'find_words':
      return <FindWordsSlideEditor {...props} />
    case 'complex_fill_blank':
      return <ComplexFillBlankSlideEditor {...props} />
    default:
      return null
  }
}

/**
 * The capsule's picker sections. The rows and their grouping come from the
 * shared builder, so the capsule and a FlashBook offer the same blocks in the
 * same order and neither one lists them out again.
 *
 * The capsule offers the rich reading blocks too. A quote, a note, a captioned
 * figure, a table and a formula are presentations of `content` (see
 * `#/lib/richBlockKinds`), and both halves of offering one exist: the capsule's
 * content editor renders the shared rich fields *and* the reading-audio field
 * (see `#/screens/CapsuleStudio/components/slides/ContentSlideEditor`), and its
 * play side prints each kind in the theme's own treatment rather than as a plain
 * paragraph. The rows are one list, so a kind added there appears in both
 * studios' pickers at once.
 */
const SLIDE_PICKER_SECTIONS = buildStudioPickerSections(RICH_PICKER_ENTRIES)

const THEMES = listThemes().map(
  (plugin: {
    id: string
    name: string
    description: string
    previewColor: string
  }) => ({
    id: plugin.id,
    name: plugin.name,
    description: plugin.description,
    previewColor: plugin.previewColor,
  }),
)

import {validateSlide} from '#/lib/capsuleValidation'
import {RICH_KIND_META, RICH_PICKER_ENTRIES} from '#/lib/richBlockKinds'
import {createSlide} from '#/lib/slideFactory'
import {buildStudioPickerSections} from '#/lib/studioBlockPicker'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'CapsuleStudio'>
export function CapsuleStudioScreen({navigation, route}: Props) {
  const {t: l} = useLingui()
  const themeIdParam = route.params?.themeId
  const _htmlThemeUrlParam = route.params?.htmlThemeUrl

  const [video, setVideo] = useState<ImagePickerAsset | null>(null)
  const [selectedThemeId, setSelectedThemeId] = useState(
    themeIdParam ?? THEMES[0]?.id ?? 'classic-quiz',
  )
  const [selectedHtmlThemeUrl, setSelectedHtmlThemeUrl] = useState<
    string | undefined
  >(_htmlThemeUrlParam ?? undefined)
  const [showSlideTypePicker, setShowSlideTypePicker] = useState(false)
  const [isPreviewVisible, setIsPreviewVisible] = useState(false)
  const [previewSlides, setPreviewSlides] = useState<Slide[]>([])
  const [slides, setSlides] = useState<Slide[]>([
    {id: 'video_0', type: 'video', contentTitle: '', contentText: ''},
  ])
  const [showThemePicker, setShowThemePicker] = useState(false)
  /**
   * The bed, held apart from the theme.
   *
   * `undefined` — nothing ever chosen — is the default and a real state: it is
   * what every capsule written before the catalogue holds, and those keep the
   * music their theme has always played. `null` is the creator taking the bed
   * away on purpose, which is why it is not the same value.
   */
  const [backgroundMusicId, setBackgroundMusicId] =
    useState<BackgroundMusicChoice>(undefined)
  const [showMusicPicker, setShowMusicPicker] = useState(false)
  /**
   * What a destructive press would remove, held until the creator confirms.
   *
   * Both cases are irreversible — a deleted slide is not in the recovery copy
   * (that keeps the capsule as it is *now*), and a deleted draft reaches every
   * device the creator signs in to — so both go through the same stop between the
   * tap and the loss.
   */
  const [pendingDelete, setPendingDelete] = useState<
    | {kind: 'slide'; id: string; index: number; label: string}
    | {kind: 'draft'; uri: string; title: string}
    | null
  >(null)
  const deleteControl = useDialogControl()
  const leaveControl = useDialogControl()
  const coverStageRef = useRef<CapsuleCoverStageHandle>(null)
  const pagerScrollRef = useRef<ScrollView>(null)
  /**
   * Test-only: a film length the cover picker should behave as if it returned.
   *
   * The cover's trimmer only exists for a film longer than a cover may run, and a
   * browser cannot hand an `<input type="file">` a real three-minute video, so the
   * fixture states the length instead (see `e2e-web/`).
   */
  const pickedFilmMsParam = route.params?.pickedFilmMs

  useEffect(() => {
    if (themeIdParam) {
      setSelectedThemeId(themeIdParam)
    }
  }, [themeIdParam])
  const [currentPage, setCurrentPage] = useState(0)
  /**
   * Whether this studio is the screen in front of the creator.
   *
   * A stack keeps the screen it came from mounted, so leaving the studio does not
   * unmount it — which is how the cover film carried on playing to an empty room
   * and was still playing when the creator came back. The cover stage gates its
   * playback on this.
   */
  const isFocused = useIsFocused()
  /**
   * The slide whose first field still owes the creator the caret.
   *
   * State, because it decides what is *rendered*: the new slide's editor is the
   * one that offers its first field the caret. It is cleared the moment the
   * slide has had its chance — whether or not a field was there to take it.
   */
  const [focusSlideId, setFocusSlideId] = useState<string | null>(null)
  const settledSlideFocus = useCallback(() => setFocusSlideId(null), [])
  const ch = useStyles()
  const c = useStudioChrome()
  const nav = useNavigation<NavigationProp>()

  const {width: windowWidth, height: windowHeight} = useWindowDimensions()
  const activeTheme = THEMES.find(th => th.id === selectedThemeId)
  const activeThemeColor = activeTheme?.previewColor ?? c.accentFill
  const activeThemeName = activeTheme?.name
  /** The theme's colour as ink on *this* appearance, not on its own preview. */
  const activeThemeInk = readableAccent(activeThemeColor, c)

  const {top: safeAreaTop, bottom: safeAreaBottom} = useSafeAreaInsets()
  const headerHeight = 60
  const tabBarHeight = 40
  const padding = 0

  const maxAvailableHeight =
    windowHeight -
    safeAreaTop -
    safeAreaBottom -
    headerHeight -
    tabBarHeight -
    padding
  const maxAvailableWidth = Math.min(windowWidth, 600) - padding

  const videoWidth = maxAvailableWidth
  const videoHeight = maxAvailableHeight

  /**
   * Whether the header can afford words.
   *
   * The header is one row of fixed-width controls with the title as the only thing
   * that gives, and it had become full: the drafts pill pushed the last control off
   * the right edge at phone width (measured in `e2e-web/capsule-drafts.spec.ts`).
   * So the two pills that can be understood from their icon alone — a bookmark and
   * an eye — drop their labels when there is not room, and keep them when there is.
   * The count stays in the drafts pill either way: it is the only part that says
   * anything the icon does not.
   */
  const roomForLabels = windowWidth >= 480

  const handleVideoAssetSelected = (asset: ImagePickerAsset) => {
    setVideo(asset)
  }

  const canPublish = useMemo(() => {
    if (slides.length < 4) return false
    return slides.every(
      sl => validateSlide(sl, m => String(m.id ?? m.message ?? '')) === null,
    )
  }, [slides])

  const publishCapsuleControl = useDialogControl()

  /** What a refused publish found, as the issues sheet lists it. */
  const [publishIssues, setPublishIssues] = useState<StudioIssue[]>([])
  const publishIssuesControl = useDialogControl()

  const handlePublishCapsule = useCallback(() => {
    // Every problem at once, in slide order — the capsule's own share of the
    // FlashBook studio's publish alert (see `#/components/studio/StudioIssuesDialog`),
    // rather than one toast per attempt naming whichever slide came first.
    const issues: StudioIssue[] = []
    if (slides.length < 4) {
      issues.push({
        key: 'capsule:slides',
        where: l`Capsule`,
        what: l`Slides`,
        message: l`Add at least three content slides.`,
      })
    }
    slides.forEach((slide, index) => {
      const problem = validateSlide(slide, l)
      if (!problem) return
      issues.push({
        key: slide.id,
        where: l`Slide ${index + 1}`,
        what: SLIDE_TYPE_META[slide.type]?.label ?? slide.type,
        message: problem,
        // Slide one is the cover, and it is the one already on the canvas: a row
        // that would jump nowhere is drawn as a statement, not a link.
        ...(index !== currentPage
          ? {onOpen: () => setCurrentPage(index)}
          : null),
      })
    })

    if (issues.length > 0) {
      setPublishIssues(issues)
      publishIssuesControl.open()
      return
    }
    publishCapsuleControl.open()
  }, [currentPage, publishCapsuleControl, publishIssuesControl, slides, l])

  const handleAddPage = useCallback(
    (type: SlideType, patch?: Partial<Slide>) => {
      // `patch` is how a presented block (FlashBook's quote, table, formula…)
      // arrives already knowing what it is. The capsule's picker adds none today,
      // but it shares the picker that does.
      const newSlide = createSlide(type, patch)
      setSlides(prev => [...prev, newSlide])
      // The picker steps onto the slide it just made, and the slide is handed
      // over ready to write in: the creator chose a block type in order to put
      // something in it, so the caret goes into its own first field rather than
      // waiting to be tapped into it. See `#/components/studio/StudioFirstFieldFocus`.
      setFocusSlideId(newSlide.id)
    },
    [],
  )

  const handleDeletePage = useCallback((pageId: string) => {
    // The cover is the capsule: there is no first screen without it.
    if (pageId === 'video_0') return
    setSlides(prev => prev.filter(sl => sl.id !== pageId))
    setCurrentPage(p => Math.max(0, p - 1))
  }, [])

  /**
   * Whether there is anything to lose at all.
   *
   * The cover's film is counted here even though nothing can bring it back: this
   * is the *guard's* question — is there anything that would be dropped? — and the
   * answer for an attached file is yes. The autosave below asks a narrower one.
   */
  const hasWork = useMemo(
    () =>
      slides.length > 1 ||
      slides.some(
        sl =>
          !!sl.videoUrl ||
          (sl.contentTitle ?? '').trim() !== '' ||
          (sl.contentText ?? '').trim() !== '',
      ),
    [slides],
  )

  // ── Drafts, the device copy, and the unsaved-work guard ───────────────────

  const agent = useAgent()
  const queryClient = useQueryClient()
  /**
   * Drafts, newest first: the server's list by author, merged with this device's
   * index. Which of the two is in front of the creator — and what happens when the
   * server cannot be asked at all — is the whole of `useCapsuleDrafts`.
   */
  const {drafts, setDrafts} = useCapsuleDrafts(agent.session?.did)

  /**
   * The cover, in the shape a *record* carries it: a title and a description, not
   * a slide. The studio puts them on `slides[0]` because that is the screen the
   * creator edits them on, and the cover slide is rebuilt from them when a draft is
   * opened — which is also how publishing already divides the capsule up.
   */
  const coverTitle = slides[0]?.contentTitle ?? ''
  const coverDescription = slides[0]?.contentText ?? ''
  /** The content slides: everything after the cover. */
  const contentSlides = useMemo(() => slides.slice(1), [slides])

  /** What a save would carry — the only thing that counts as a change. */
  const contentSignature = capsuleContentSignature({
    title: coverTitle,
    description: coverDescription,
    themeId: selectedThemeId,
    backgroundMusicId,
    htmlThemeUrl: selectedHtmlThemeUrl,
    slides: contentSlides,
  })

  /** Fingerprint of the capsule as it was last saved (draft or published). */
  const [savedSignature, setSavedSignature] = useState<string | null>(null)
  /** Draft the current work was loaded from, if any. */
  const [loadedDraftUri, setLoadedDraftUri] = useState<string | null>(null)
  /**
   * That draft's own `createdAt`. Saving again updates the record in place, and
   * the record is replaced wholesale, so the original date has to travel with it
   * or a two-week-old draft would claim it was created just now.
   */
  const [loadedDraftCreatedAt, setLoadedDraftCreatedAt] = useState<
    string | null
  >(null)
  /** Draft being fetched by the drafts list (shows a spinner on its row). */
  const [openingDraftUri, setOpeningDraftUri] = useState<string | null>(null)
  /** What the unsaved-work guard is currently protecting. */
  const [leaveIntent, setLeaveIntent] = useState<
    {kind: 'leave'} | {kind: 'replace'; draftTitle: string}
  >({kind: 'leave'})
  /** A draft save is in flight — the leave dialog stays open until it lands. */
  const [savingDraft, setSavingDraft] = useState(false)
  const draftsControl = useDialogControl()
  const recoveryControl = useDialogControl()
  /** Set right before we let a guarded navigation through. */
  const allowLeaveRef = useRef(false)
  /** What to do once the leave dialog is resolved. */
  const pendingLeaveRef = useRef<null | (() => void)>(null)

  /**
   * The capsule the studio started from, for deciding whether a copy on the device
   * is *this* capsule's unfinished work.
   *
   * It moves when a draft is opened, because that is when the studio learns which
   * capsule it is holding. Deliberately not `savedSignature`, which moves on every
   * save: the question here is "was this copy taken from the capsule I am holding?",
   * not "has anything changed since I last saved?".
   */
  const [baseline, setBaseline] = useState<{
    draftUri: string | null
    signature: string
  }>(() => ({draftUri: null, signature: contentSignature}))
  /**
   * The copy of this capsule left behind by the session that ended before this
   * one.
   *
   * Read once, at open, and held for the life of the screen. The autosave rewrites
   * the device's slot within a second of the first edit, so a copy re-read later
   * would be gone by the time it mattered — and the moment it matters most is when
   * a draft is opened, which is exactly when a capsule's unfinished work lives.
   */
  const [lastSession] = useState<CapsuleSnapshot | null>(() =>
    getCapsuleSnapshot(),
  )
  /** Set once the offer has been answered, either way, so it cannot reappear. */
  const [recoveryAnswered, setRecoveryAnswered] = useState(false)

  /**
   * Whether this creator would lose something by leaving.
   *
   * A copy is being kept on this device as they go (see `autosave`), so leaving is
   * no longer the catastrophe it was — but the copy is *this device's*, and the draft
   * is what makes the capsule real. Work that matches the last save (an untouched
   * draft) does not count.
   */
  const hasUnsavedWork = contentSignature !== savedSignature && hasWork

  /**
   * The copy worth handing back, if there is one.
   *
   * Derived rather than stored, so every way the question can change — a draft
   * opened, the offer answered — is one input to one expression, and there is no
   * path that leaves a stale offer on the screen.
   */
  const recoveryOffer = useMemo(
    () =>
      recoveryAnswered ? null : offerableCapsuleSnapshot(lastSession, baseline),
    [baseline, lastSession, recoveryAnswered],
  )

  /** The capsule as this device should keep it, read at the moment it is written. */
  const capsuleForRecovery = useCallback(
    (): CapsuleRecoveredDraft => ({
      title: coverTitle,
      description: coverDescription,
      themeId: selectedThemeId,
      backgroundMusicId: musicChoiceToRecord(backgroundMusicId),
      htmlThemeUrl: selectedHtmlThemeUrl,
      slides: contentSlides,
    }),
    [
      backgroundMusicId,
      contentSlides,
      coverDescription,
      coverTitle,
      selectedHtmlThemeUrl,
      selectedThemeId,
    ],
  )

  /**
   * The copy of this capsule on this device, kept as it is written.
   *
   * It writes to the device and nothing else. Saving a draft stays the creator's
   * decision — that is the whole design of the drafts feature — and this is the app
   * remembering for them in the meantime. `keep` is deliberately narrower than
   * `hasUnsavedWork`: it asks what the autosave needs to know — has anything changed
   * *since this screen opened*? A session that is only reading what the previous one
   * left has nothing to keep, and — the part that matters — must not write over the
   * copy of that session's work while it is still being offered back. And work that
   * has just been saved as a draft is not unsaved either, so the copy goes with it.
   */
  const autosave = useCapsuleAutosave({
    signature: contentSignature,
    keep: hasUnsavedWork && contentSignature !== baseline.signature,
    draftUri: loadedDraftUri,
    take: capsuleForRecovery,
  })

  /**
   * The offer is a decision, so it arrives as a dialog rather than a banner that
   * could be scrolled past and forgotten.
   *
   * It asks again after a navigation `state` event, and that second asking is not
   * belt-and-braces — it is the only reason the offer is ever seen on the web. The
   * shell dismisses every open dialog on each of those events, including the one
   * that brings this screen in, so the dialog opened on mount is swept away before
   * it has been painted. Only *after* the sweep has finished does the asking
   * survive, hence the `setTimeout`; to a creator this is a frame, not a delay. The
   * same measurement, and the same fix, as the FlashBook studio's offer.
   */
  useEffect(() => {
    if (!recoveryOffer) return
    recoveryControl.open()
    let pending: ReturnType<typeof setTimeout> | null = null
    const sub = navigation.addListener('state', () => {
      if (pending) return
      pending = setTimeout(() => {
        pending = null
        recoveryControl.open()
      }, 0)
    })
    return () => {
      if (pending) clearTimeout(pending)
      sub()
    }
  }, [navigation, recoveryControl, recoveryOffer])

  /** Resolves the guard by running whatever the dialog was protecting. */
  const resolvePending = useCallback(() => {
    const action = pendingLeaveRef.current
    pendingLeaveRef.current = null
    // Closing runs the pending action after the dialog has gone, so the creator
    // never sees the studio flash back to life behind the overlay.
    if (action) {
      leaveControl.close(action)
    } else {
      leaveControl.close()
    }
  }, [leaveControl])

  /**
   * Every way out of the studio funnels through here: with unpublished work it asks
   * first (save a draft or discard), otherwise it just leaves.
   *
   * The pending thunk flags the exit as allowed, so re-issuing it after the dialog
   * closes doesn't trip the `beforeRemove` guard a second time.
   */
  const requestLeave = useCallback(
    (action: () => void) => {
      if (!hasUnsavedWork) {
        action()
        return
      }
      setLeaveIntent({kind: 'leave'})
      pendingLeaveRef.current = () => {
        allowLeaveRef.current = true
        action()
      }
      leaveControl.open()
    },
    [hasUnsavedWork, leaveControl],
  )

  // Swipe-back, browser back and hardware back never reach the header button, so
  // the guard has to listen for them too — otherwise the warning would only cover
  // the one exit the creator is least likely to use.
  useEffect(() => {
    const sub = navigation.addListener('beforeRemove', e => {
      if (allowLeaveRef.current || !hasUnsavedWork) return
      e.preventDefault()
      setLeaveIntent({kind: 'leave'})
      pendingLeaveRef.current = () => {
        allowLeaveRef.current = true
        navigation.dispatch(e.data.action)
      }
      leaveControl.open()
    })
    return sub
  }, [hasUnsavedWork, leaveControl, navigation])

  const handleBack = useCallback(() => {
    requestLeave(() => {
      if (navigation.canGoBack()) {
        navigation.goBack()
        return
      }
      nav.navigate('Home')
    })
  }, [navigation, nav, requestLeave])

  /**
   * A slide's name, used by the pager, the delete confirmation and the report.
   * The creator's own title when there is one, otherwise the block type — never
   * the raw slide type, which is how "6.fill_blank" used to reach the screen.
   */
  const labelForSlide = useCallback(
    (sl: Slide) =>
      sl.contentTitle?.trim() ||
      (sl.type === 'video'
        ? l`Cover`
        : sl.type === 'content'
          ? // A rich reading block is named for what it is — a quote is not "a
            // content slide" to the person who added one.
            l(RICH_KIND_META[sl.contentKind ?? 'text'].label)
          : (SLIDE_TYPE_META[sl.type]?.label ?? sl.type)),
    [l],
  )

  /** The "+" path: the slide on screen has to be valid before another is added. */
  const askToAddSlide = useCallback(() => {
    const sl = slides[currentPage]
    if (sl) {
      const err = validateSlide(sl, l)
      if (err) {
        Toast.show(err, 'circle-exclamation')
        return
      }
    }
    setShowSlideTypePicker(true)
  }, [slides, currentPage, l])

  /**
   * Continue, from the cover: the next slide, or the picker when there is none.
   *
   * It is the one action on the first screen, so it has to lead somewhere real —
   * the capsule's first content slide when there is one, and the block picker that
   * creates it when there is not. A capsule that is only a cover would otherwise
   * have a primary button that does nothing.
   */
  const handleCoverContinue = useCallback(() => {
    if (slides.length > 1) {
      setCurrentPage(1)
      return
    }
    setShowSlideTypePicker(true)
  }, [slides.length])

  /**
   * Drops the changes — including this device's copy of them.
   *
   * A draft this work came from is left untouched: discarding means the *unsaved*
   * work goes, and a recovery copy is exactly that. Leaving it behind would mean the
   * studio offering back the evening the creator just threw away.
   */
  const handleDiscard = useCallback(() => {
    autosave.discard()
    resolvePending()
  }, [autosave, resolvePending])

  /**
   * Puts the copy from the previous session back.
   *
   * The cover is rebuilt from the record's own fields, exactly as opening a draft
   * does — a snapshot stores the record's shape, not the screen's. Both fields the
   * studio needs to go on saving correctly are already right: the offer is only ever
   * made against the capsule the studio is holding, so a copy that came from a draft
   * arrives after that record was loaded, and saving afterwards updates it rather
   * than leaving a second half-finished capsule behind.
   */
  const applyRecovery = useCallback(() => {
    const snapshot = recoveryOffer
    if (!snapshot) return
    const {capsule} = snapshot
    setSlides([
      {
        id: 'video_0',
        type: 'video',
        contentTitle: capsule.title,
        contentText: capsule.description,
      },
      ...capsule.slides,
    ])
    // A copy cannot carry a local file, so whatever film was attached to the
    // capsule being replaced does not belong to this one.
    setVideo(null)
    setSelectedThemeId(capsule.themeId)
    setBackgroundMusicId(musicChoiceFromRecord(capsule.backgroundMusicId))
    setSelectedHtmlThemeUrl(capsule.htmlThemeUrl)
    setCurrentPage(0)
    setRecoveryAnswered(true)
    Toast.show(l`Picked up where you left off.`)
  }, [l, recoveryOffer])

  /**
   * Throws the copy away and starts clean — the creator's answer, not a default.
   *
   * The device's copy goes with it, because a copy nobody wants is a dialog
   * everybody meets twice.
   */
  const discardRecovery = useCallback(() => {
    setRecoveryAnswered(true)
    autosave.discard()
  }, [autosave])

  /**
   * Keeps the capsule on the creator's PDS as a `status: 'draft'` record — no post,
   * so it stays private — and remembers it locally so the drafts list can offer it
   * back.
   */
  const handleSaveDraft = useCallback(async () => {
    const did = agent.session?.did
    if (!did) {
      Toast.show(
        l`You need to be signed in to save a draft.`,
        'circle-exclamation',
      )
      return
    }
    setSavingDraft(true)
    try {
      const {uri, createdAt} = await saveCapsuleDraft({
        agent,
        did,
        title: coverTitle,
        description: coverDescription,
        themeId: selectedThemeId,
        backgroundMusicId: musicChoiceToRecord(backgroundMusicId),
        htmlThemeUrl: selectedHtmlThemeUrl,
        slides: contentSlides,
        // Saving again during the same session updates the record the first save
        // created, rather than leaving a trail of near-identical drafts.
        draftUri: loadedDraftUri,
        createdAt: loadedDraftCreatedAt,
      })
      setLoadedDraftUri(uri)
      setLoadedDraftCreatedAt(createdAt)
      setDrafts(
        rememberCapsuleDraft({
          uri,
          title: coverTitle.trim() || l`Untitled capsule`,
          slideCount: contentSlides.length,
          themeId: selectedThemeId,
          updatedAt: new Date().toISOString(),
        }),
      )
      // The server list is the authority now, so it has to hear about this save.
      void queryClient.invalidateQueries({
        queryKey: createActorCapsuleDraftsQueryKey(did),
      })
      setSavedSignature(contentSignature)
      Toast.show(l`Draft saved — find it under “Drafts”.`)
      resolvePending()
    } catch (e) {
      console.error('Failed to save capsule draft', e)
      Toast.show(l`Could not save the draft.`, 'circle-exclamation')
    } finally {
      setSavingDraft(false)
    }
  }, [
    agent,
    contentSignature,
    contentSlides,
    coverDescription,
    coverTitle,
    l,
    loadedDraftCreatedAt,
    loadedDraftUri,
    queryClient,
    resolvePending,
    selectedHtmlThemeUrl,
    selectedThemeId,
    // Stable (it is the `useState` setter inside `useCapsuleDrafts`), but the rule
    // cannot see through a custom hook, so it is named here.
    setDrafts,
  ])

  /**
   * Pulls a saved draft back into the studio so it can be finished.
   *
   * The list stays *open* while this runs and is closed by the load itself, once the
   * capsule is in the studio — so the wait is visible on the row that was tapped
   * instead of on a dialog that has already gone.
   */
  const loadDraft = useCallback(
    async (draft: CapsuleDraftRef) => {
      const did = agent.session?.did
      if (!did) {
        // A tap that does nothing and says nothing is a dead button. In the app this
        // cannot happen — the studio is behind `requireAuth` — but a drafts row that
        // silently ignores a press is not a state worth leaving in.
        Toast.show(l`Could not open this draft.`, 'circle-exclamation')
        return
      }
      setOpeningDraftUri(draft.uri)
      try {
        const atUri = new AtUri(draft.uri)
        const res = await agent.com.atproto.repo.getRecord({
          repo: atUri.hostname,
          collection: atUri.collection,
          rkey: atUri.rkey,
        })
        const value = res.data.value as Record<string, unknown>
        const savedSlides = (value.slides as Slide[] | undefined) ?? []
        const htmlThemeUrl = (
          value.behaviorSettings as {htmlThemeUrl?: string} | undefined
        )?.htmlThemeUrl
        const nextTitle = (value.title as string | undefined) ?? ''
        const nextDescription = (value.description as string | undefined) ?? ''
        const nextThemeId =
          (value.themeId as string | undefined) ??
          THEMES[0]?.id ??
          'classic-quiz'

        setSlides([
          {
            id: 'video_0',
            type: 'video',
            contentTitle: nextTitle,
            contentText: nextDescription,
          },
          ...savedSlides,
        ])
        // A draft carries no film: publishing uploads it as the post's video
        // embed, and a draft has no post.
        setVideo(null)
        // In the record's spelling here, so the loaded signature below matches
        // the one the saved capsule had: an opened draft is not unsaved work.
        const nextBackgroundMusicId = musicChoiceFromRecord(
          value.backgroundMusicId,
        )
        setSelectedThemeId(nextThemeId)
        setBackgroundMusicId(nextBackgroundMusicId)
        setSelectedHtmlThemeUrl(htmlThemeUrl)
        // The capsule now matches what is on the server, so leaving it untouched
        // must not read as unsaved work.
        const loadedSignature = capsuleContentSignature({
          title: nextTitle,
          description: nextDescription,
          themeId: nextThemeId,
          backgroundMusicId: nextBackgroundMusicId,
          htmlThemeUrl,
          slides: savedSlides,
        })
        setSavedSignature(loadedSignature)
        // And this is the capsule the studio is holding, which is the only moment it
        // can learn that a copy on the device belongs to *this* draft.
        setBaseline({draftUri: draft.uri, signature: loadedSignature})
        setCurrentPage(0)
        setLoadedDraftUri(draft.uri)
        setLoadedDraftCreatedAt(
          typeof value.createdAt === 'string' ? value.createdAt : null,
        )
        draftsControl.close()
        Toast.show(l`Draft loaded.`)
      } catch (e) {
        console.error('Failed to open capsule draft', e)
        Toast.show(l`Could not open this draft.`, 'circle-exclamation')
      } finally {
        setOpeningDraftUri(null)
      }
    },
    [agent, draftsControl, l],
  )

  /**
   * Opening a draft replaces whatever is in the studio, so when there is unsaved
   * work it goes through the same guard as leaving — where the creator can save the
   * current capsule first.
   *
   * With nothing to lose, the list stays up and shows the row working; the load
   * closes it when the capsule is in the studio. (With unsaved work the list has to
   * go first — the leave dialog is the next thing the creator has to answer, and two
   * dialogs cannot be asked at once.)
   */
  const handleOpenDraft = useCallback(
    (draft: CapsuleDraftRef) => {
      if (!hasUnsavedWork) {
        void loadDraft(draft)
        return
      }
      setLeaveIntent({kind: 'replace', draftTitle: draft.title})
      draftsControl.close(() => {
        pendingLeaveRef.current = () => void loadDraft(draft)
        leaveControl.open()
      })
    },
    [draftsControl, hasUnsavedWork, leaveControl, loadDraft],
  )

  /**
   * Deleting a draft is irreversible and reaches every device the creator uses, so
   * it asks first — and says so, because the older behaviour only hid the row here.
   */
  const handleRemoveDraft = useCallback(
    (draft: CapsuleDraftRef) => {
      draftsControl.close(() => {
        setPendingDelete({
          kind: 'draft',
          uri: draft.uri,
          title: draft.title,
        })
        deleteControl.open()
      })
    },
    [deleteControl, draftsControl],
  )

  /**
   * Runs whatever the delete dialog was confirming, once the creator has said so.
   *
   * A deleted draft is removed from this device's index *first*, so the row cannot
   * come back before the request lands — the creator asked for it to be gone, and a
   * list that argues with them is worse than a list that is briefly ahead of the
   * server. The record itself is deleted on the PDS, which is what makes it gone on
   * every other device too.
   */
  const confirmPendingDelete = useCallback(() => {
    const pending = pendingDelete
    setPendingDelete(null)
    if (!pending) return
    if (pending.kind === 'slide') {
      handleDeletePage(pending.id)
      return
    }
    const did = agent.session?.did
    setDrafts(forgetCapsuleDraft(pending.uri))
    // If the work in the studio came from this draft, it is now a capsule that has
    // never been saved — saving again must create a record, not overwrite a
    // deleted one.
    if (loadedDraftUri === pending.uri) {
      setLoadedDraftUri(null)
      setLoadedDraftCreatedAt(null)
    }
    if (!did) return
    void deleteCapsuleDraft({agent, did, uri: pending.uri}).catch(e => {
      console.error('Failed to delete capsule draft', e)
      Toast.show(l`Could not delete the draft.`, 'circle-exclamation')
    })
  }, [agent, handleDeletePage, l, loadedDraftUri, pendingDelete, setDrafts])

  /**
   * Writes what a block's editor changed onto the slide.
   *
   * Everything the editor hands over is kept, and only the shapes that need
   * normalising are touched. This used to be a hand-written whitelist — one line
   * per field — which meant a block whose editor asked for something the list had
   * not heard of wrote into the studio and was *silently dropped*: a callout's
   * tone, a table's cells, a formula's LaTeX, a quote's attribution, a reading
   * block's recorded voice. A creator typed into a block and the block forgot.
   *
   * A block that cannot keep what was typed is worse than one that cannot be
   * added, because nothing on screen says so. The book studio has always merged
   * the whole update; this is that same rule, so a new field on a block reaches
   * storage the day the editor offers it.
   */
  const handlePageUpdate = useCallback(
    (pageId: string, data: Partial<Slide>) => {
      setSlides(prev => {
        return prev.map(sl => {
          if (sl.id !== pageId) return sl
          return {
            ...sl,
            ...data,
            // The one shape that is not carried as-is: an option needs an id to
            // be answerable, and an editor that has not made one yet gets it here.
            ...(data.options !== undefined
              ? {
                  options: data.options.map((o, i) => ({
                    ...o,
                    id: o.id ?? `o_${i}`,
                  })),
                }
              : {}),
          }
        })
      })
    },
    [],
  )

  return (
    <Layout.Screen minimalShell testID="capsuleStudioScreen">
      <CapsulePreviewModal
        visible={isPreviewVisible}
        slides={previewSlides}
        themeId={selectedThemeId}
        htmlThemeUrl={selectedHtmlThemeUrl}
        title={slides[0]?.contentTitle ?? ''}
        onClose={() => setIsPreviewVisible(false)}
      />
      <StudioIssuesDialog
        control={publishIssuesControl}
        title={l`This capsule isn't ready to publish`}
        caption={
          publishIssues.length === 1
            ? l`One thing needs fixing before a learner can play this. Nothing has been published.`
            : l`${publishIssues.length} things need fixing before a learner can play this. Nothing has been published.`
        }
        issues={publishIssues}
      />
      <PublishCapsuleDialog
        control={publishCapsuleControl}
        title={slides[0]?.contentTitle ?? ''}
        description={slides[0]?.contentText ?? ''}
        slides={slides.slice(1)}
        video={video}
        themeId={selectedThemeId}
        backgroundMusicId={musicChoiceToRecord(backgroundMusicId)}
        htmlThemeUrl={selectedHtmlThemeUrl}
        // The minute the creator chose on the cover, carried onto the record so
        // the published capsule opens there rather than at the film's start.
        coverStartMs={slides[0]?.videoStartMs}
        onSuccess={() => {
          // Published — there is nothing left to warn about, and nothing left to
          // recover: the capsule is on the record now, and a copy on the device
          // would only offer the creator their own published work back.
          allowLeaveRef.current = true
          autosave.discard()
          /*
            And a capsule published *from* a draft has to take the draft with it.

            Publishing writes a new record rather than converting this one, so
            without this the creator would find the capsule they just published
            sitting in their unfinished list — under a row that says it is theirs to
            keep editing.
          */
          const did = agent.session?.did
          if (loadedDraftUri) {
            setDrafts(forgetCapsuleDraft(loadedDraftUri))
            if (did) {
              void deleteCapsuleDraft({agent, did, uri: loadedDraftUri}).catch(
                e => console.error('Failed to clear published draft', e),
              )
            }
          }
          if (navigation.canGoBack()) {
            navigation.popToTop()
          }
          nav.navigate('Home')
        }}
      />

      <StudioConfirmDialog
        control={deleteControl}
        title={
          pendingDelete?.kind === 'draft'
            ? l`Delete this draft?`
            : pendingDelete?.kind === 'slide'
              ? l`Delete slide ${pendingDelete.index + 1}?`
              : l`Delete this slide?`
        }
        message={
          pendingDelete?.kind === 'draft'
            ? l`“${pendingDelete.title}” will be deleted from your drafts everywhere, not just on this device. This cannot be undone.`
            : pendingDelete?.kind === 'slide'
              ? l`"${pendingDelete.label}" and everything written on it will be lost. This can't be undone.`
              : ''
        }
        confirmLabel={
          pendingDelete?.kind === 'draft' ? l`Delete draft` : l`Delete slide`
        }
        onConfirm={confirmPendingDelete}
      />
      {/*
        The guard that stands between the creator and unsaved work.

        It used to be a warning and nothing else — "leaving now throws away six
        slides" — which announced the loss rather than preventing it. Now both
        answers are real: keep the capsule as a draft, or discard it on purpose.
      */}
      <CapsuleLeaveDialog
        control={leaveControl}
        busy={savingDraft}
        slideCount={slides.length}
        intent={leaveIntent.kind}
        draftTitle={
          leaveIntent.kind === 'replace' ? leaveIntent.draftTitle : undefined
        }
        onSaveDraft={() => void handleSaveDraft()}
        onDiscard={handleDiscard}
      />
      <CapsuleDraftsDialog
        control={draftsControl}
        drafts={drafts}
        openingUri={openingDraftUri}
        onOpen={handleOpenDraft}
        onRemove={handleRemoveDraft}
      />
      {/*
        The copy from the session that ended before this one.

        Mounted whether or not there is one — a dialog control can only open a dialog
        that is on the screen — and it renders as nothing at all when there is no
        offer, which is nearly always.
      */}
      <CapsuleRecoveryDialog
        control={recoveryControl}
        snapshot={recoveryOffer}
        onRestore={applyRecovery}
        onDiscard={discardRecovery}
      />

      <Layout.Center style={{flex: 1}}>
        {/* Custom creator header */}
        <View style={ch.header} testID="capsuleStudioHeader">
          {/* Back */}
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Back`}
            accessibilityHint={l`Asks before discarding an unfinished capsule`}
            testID="capsuleBackBtn"
            onPress={handleBack}
            style={ch.backBtn}>
            <Feather name="arrow-left" size={18} color={c.text} />
          </TouchableOpacity>

          {/* Title — with the two facts the creator keeps having to go and look
              up: how long the capsule is, and which theme it wears. */}
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Capsule title`}
            accessibilityHint={l`Jumps to the cover and edits its title`}
            testID="capsuleTitleBtn"
            onPress={() => {
              setCurrentPage(0)
              coverStageRef.current?.beginTitleEdit()
            }}
            style={ch.titleBtn}>
            <Text style={ch.titleTxt} numberOfLines={1}>
              {slides[0]?.contentTitle || l`Untitled Capsule`}
            </Text>
            <Text style={ch.titleSub} numberOfLines={1}>
              {l`${slides.length} slides`}
              {activeThemeName ? ` · ${activeThemeName}` : ''}
            </Text>
          </TouchableOpacity>

          {/* Theme pill. The dot keeps the theme's own colour — that is the
              identity — but the label and the ring go through `readableAccent`,
              because several theme colours (amber, mint) are tuned for dark
              cards and wash out as ink on a light studio. */}
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Change the capsule theme`}
            accessibilityHint={l`Opens the theme picker`}
            testID="capsuleThemeBtn"
            onPress={() => setShowThemePicker(true)}
            style={[
              ch.themePill,
              {
                borderColor: activeThemeInk + '55',
                backgroundColor: activeThemeInk + '1A',
              },
            ]}>
            <View style={[ch.themeDot, {backgroundColor: activeThemeColor}]} />
            <Text
              style={[ch.themeTxt, {color: activeThemeInk}]}>{l`Theme`}</Text>
          </TouchableOpacity>

          {/*
            Background music — the theme's neighbour, and its own setting.

            Beside the palette rather than inside it, because the creator's next
            question after "how does this look" is "what does it sound like", and
            because the two are independent: changing one must not disturb the
            other. The label is the one thing here that can be dropped when the
            header is tight — the icon and the accessible name carry it.
          */}
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={
              backgroundMusicId === null
                ? l`Background music: none`
                : l`Background music`
            }
            accessibilityHint={l`Opens the music browser`}
            testID="capsuleMusicBtn"
            onPress={() => setShowMusicPicker(true)}
            style={[
              ch.themePill,
              backgroundMusicId != null && {
                borderColor: c.accent + '55',
                backgroundColor: c.accent + '1A',
              },
            ]}>
            <Feather
              name={backgroundMusicId === null ? 'volume-x' : 'music'}
              size={13}
              color={backgroundMusicId != null ? c.accent : c.textMuted}
            />
            {roomForLabels && (
              <Text
                style={[
                  ch.themeTxt,
                  {color: backgroundMusicId != null ? c.accent : c.textMuted},
                ]}>
                {l`Music`}
              </Text>
            )}
          </TouchableOpacity>

          {/*
            Drafts. Shown only when there are some, because the list is how
            unfinished work is found again — a button that opens an empty sheet is
            a button that teaches the creator not to press it.
          */}
          {drafts.length > 0 && (
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Unpublished drafts (${drafts.length})`}
              accessibilityHint={l`Opens your saved drafts`}
              onPress={() => draftsControl.open()}
              testID="capsuleDraftsBtn"
              style={ch.testerBtn}>
              <Feather name="bookmark" size={13} color={c.textMuted} />
              <Text style={ch.testerTxt}>
                {roomForLabels
                  ? l`Drafts (${drafts.length})`
                  : `${drafts.length}`}
              </Text>
            </TouchableOpacity>
          )}

          {/* Preview */}
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Preview`}
            accessibilityHint={l`Plays the capsule's slides without publishing`}
            disabled={slides.length <= 1}
            onPress={() => {
              setPreviewSlides(slides.slice(1))
              setIsPreviewVisible(true)
            }}
            style={[ch.testerBtn, slides.length <= 1 && {opacity: 0.4}]}>
            <Feather name="eye" size={13} color={c.textMuted} />
            {roomForLabels && <Text style={ch.testerTxt}>{l`Preview`}</Text>}
          </TouchableOpacity>

          {/* Publish */}
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityHint={l`Checks every slide, then opens publishing`}
            testID="capsulePublishBtn"
            onPress={handlePublishCapsule}
            style={[ch.publishBtn, !canPublish && {opacity: 0.5}]}>
            <Text style={ch.publishTxt}>{l`Publish`}</Text>
          </TouchableOpacity>
        </View>

        {/*
          Whether the capsule is being kept.

          Autosave nobody can see is indistinguishable from autosave that stopped
          working, and the point of the feature is that a creator can stop worrying
          about work they are in the middle of — so the studio says it, quietly, in
          one line that changes only when it has something to say.
        */}
        <StudioAutosaveNote
          state={autosave.state}
          testID="capsuleAutosaveNote"
        />

        <View style={[a.align_center, a.justify_center, {flex: 1}]}>
          <View
            testID="capsuleStudioCanvas"
            style={[
              a.overflow_hidden,
              {
                // The editor's own surface. It used to be the recessed `stage`
                // desk, which on a dark device framed a phone-sized island of
                // near-black in the middle of the studio — the page is the
                // work, so it is raised, and the desk colour now only appears
                // behind the sheets.
                backgroundColor: c.surface,
                width: videoWidth,
                height: videoHeight,
              },
              a.relative,
            ]}>
            {/*
              The cover, or the slide.

              The cover is not a slide editor: it is the studio's first screen and
              it gets the whole canvas — the film, the words, and the two controls
              over the art. Everything after it is an editor like any other.
            */}
            <View style={[a.absolute, a.inset_0]}>
              {currentPage === 0 && slides[0] ? (
                <CapsuleCoverStage
                  ref={coverStageRef}
                  slide={slides[0]}
                  active={isFocused}
                  onChange={data => handlePageUpdate(slides[0].id, data)}
                  onVideoAssetSelected={handleVideoAssetSelected}
                  onContinue={handleCoverContinue}
                  seededFilmMs={IS_DEV ? pickedFilmMsParam : undefined}
                />
              ) : (
                slides[currentPage] && (
                  <FirstFieldFocus
                    active={focusSlideId === slides[currentPage].id}
                    onSettled={settledSlideFocus}>
                    <SlideEditorOverlay
                      slide={slides[currentPage]}
                      themeColor={activeThemeColor}
                      onUpdate={(data: Partial<Slide>) =>
                        handlePageUpdate(slides[currentPage].id, data)
                      }
                      onPreview={() => {
                        const s = slides[currentPage]
                        if (s) {
                          setPreviewSlides([s])
                          setIsPreviewVisible(true)
                        }
                      }}
                    />
                  </FirstFieldFocus>
                )
              )}
            </View>

            {/* Slide actions. These float over the editor — which may be a video,
                a photo or a plain page — so like every other control that sits
                on unknown content they use the chrome's canvas float, dark with
                white ink in both appearances, rather than the studio surface. */}
            <View style={[a.absolute, ch.actionRow, a.z_50]}>
              {currentPage > 0 && (
                <TouchableOpacity
                  accessibilityRole="button"
                  accessibilityLabel={l`Delete slide ${currentPage + 1}`}
                  accessibilityHint={l`Asks for confirmation first`}
                  onPress={() => {
                    const sl = slides[currentPage]
                    setPendingDelete({
                      kind: 'slide',
                      id: sl.id,
                      index: currentPage,
                      label: labelForSlide(sl),
                    })
                    deleteControl.open()
                  }}
                  testID="capsuleDeleteSlideBtn"
                  style={[ch.canvasChip, ch.canvasChipDanger]}>
                  <TrashIcon size="sm" fill={c.onCanvas} />
                  <Text style={ch.canvasChipTxt}>{l`Delete`}</Text>
                </TouchableOpacity>
              )}
              <View style={[a.flex_row, a.gap_sm]}>
                {currentPage > 1 && (
                  <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel={l`Move this slide earlier`}
                    accessibilityHint={l`Swaps it with the slide before it`}
                    onPress={() => {
                      setSlides(prev => {
                        const next = [...prev]
                        const i = currentPage
                        ;[next[i - 1], next[i]] = [next[i], next[i - 1]]
                        return next
                      })
                      setCurrentPage(p => p - 1)
                    }}
                    style={ch.canvasChip}>
                    <ArrowLeft size="sm" fill={c.onCanvas} />
                  </TouchableOpacity>
                )}
                {currentPage >= 1 && currentPage < slides.length - 1 && (
                  <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel={l`Move this slide later`}
                    accessibilityHint={l`Swaps it with the slide after it`}
                    onPress={() => {
                      setSlides(prev => {
                        const next = [...prev]
                        const i = currentPage
                        ;[next[i], next[i + 1]] = [next[i + 1], next[i]]
                        return next
                      })
                      setCurrentPage(p => p + 1)
                    }}
                    style={ch.canvasChip}>
                    <ArrowRight size="sm" fill={c.onCanvas} />
                  </TouchableOpacity>
                )}
              </View>
            </View>

            {/* The shared block picker: same sheet, same rows as FlashBook. */}
            {showSlideTypePicker && (
              <StudioBlockPicker
                title={l`Add a slide`}
                subtitle={l`Pick what the reader does on this slide`}
                sections={SLIDE_PICKER_SECTIONS}
                footnote={l`Use the arrows on a slide to reorder it.`}
                testID="capsuleSlidePicker"
                onClose={() => setShowSlideTypePicker(false)}
                onSelect={entry => {
                  handleAddPage(entry.type, entry.patch)
                  setCurrentPage(slides.length)
                  setShowSlideTypePicker(false)
                }}
              />
            )}

            {/* Theme picker overlay */}
            {showThemePicker && (
              <View style={[ch.pickerSheet, ch.themeSheet]}>
                <View style={ch.sheetHandle} />
                <View style={ch.sheetHeader}>
                  <View style={{flex: 1, minWidth: 0}}>
                    <Text style={ch.sheetTitle}>{l`Choose a theme`}</Text>
                    <Text style={ch.sheetSub}>
                      {l`The theme restyles every slide a reader will see`}
                    </Text>
                  </View>
                  <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel={l`Close`}
                    accessibilityHint={l`Keeps the current theme`}
                    onPress={() => setShowThemePicker(false)}
                    style={ch.sheetClose}>
                    <Feather name="x" size={16} color={c.text} />
                  </TouchableOpacity>
                </View>
                <View style={{flex: 1, overflow: 'hidden'}}>
                  <CapsuleThemeSelectorView
                    hideHeader
                    onBack={() => setShowThemePicker(false)}
                    onSelect={(themeId, htmlThemeUrl) => {
                      setSelectedThemeId(themeId)
                      setSelectedHtmlThemeUrl(htmlThemeUrl ?? undefined)
                      setShowThemePicker(false)
                    }}
                    showSafeAreaTop={false}
                  />
                </View>
              </View>
            )}

            {/*
              The music browser, beside the theme picker: two independent
              settings, changed in two sheets that never disturb each other.
            */}
            {showMusicPicker && (
              <BackgroundMusicSheet
                selected={backgroundMusicId}
                onSelect={setBackgroundMusicId}
                onClose={() => setShowMusicPicker(false)}
                testID="capsuleMusicSheet"
              />
            )}
          </View>
        </View>

        {/* Slide bar. This used to be the feed's `TabBar`: an underlined text
            strip that belongs to the home timeline, and it leaked the raw slide
            type into the UI ("6.fill_blank"). A studio bar shows numbered chips
            with real names and carries the "add" affordance, so the canvas is
            free for the slide itself. */}
        <View style={ch.pagerBar} testID="capsulePager">
          <ScrollView
            ref={pagerScrollRef}
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={ch.pagerChips}>
            {slides.map((sl, i) => {
              const selected = i === currentPage
              return (
                <TouchableOpacity
                  accessibilityRole="button"
                  accessibilityLabel={l`Go to slide ${i + 1}`}
                  accessibilityHint={l`Switches to this slide`}
                  key={sl.id}
                  onPress={() => setCurrentPage(i)}
                  onLayout={e => {
                    // Keep the slide being edited in view as the capsule grows.
                    if (i === currentPage) {
                      pagerScrollRef.current?.scrollTo({
                        x: Math.max(0, e.nativeEvent.layout.x - 12),
                        animated: true,
                      })
                    }
                  }}
                  testID={`capsuleSlideChip-${i}`}
                  style={[ch.pagerChip, selected && ch.pagerChipActive]}>
                  <View
                    style={[
                      ch.pagerChipNum,
                      selected && ch.pagerChipNumActive,
                    ]}>
                    <Text
                      style={[
                        ch.pagerChipNumTxt,
                        selected && ch.pagerChipNumTxtActive,
                      ]}>
                      {i + 1}
                    </Text>
                  </View>
                  <Text
                    numberOfLines={1}
                    style={[
                      ch.pagerChipTxt,
                      selected && ch.pagerChipTxtActive,
                    ]}>
                    {labelForSlide(sl)}
                  </Text>
                </TouchableOpacity>
              )
            })}
          </ScrollView>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Add slide`}
            accessibilityHint={l`Appends a new slide to this capsule`}
            onPress={askToAddSlide}
            testID="capsuleAddSlideBtn"
            style={ch.pagerAdd}>
            <Plus size="xs" fill={c.onAccent} />
            <Text style={ch.pagerAddTxt}>{l`Add slide`}</Text>
          </TouchableOpacity>
        </View>
      </Layout.Center>
    </Layout.Screen>
  )
}

const makeStyles = (c: StudioChrome) =>
  StyleSheet.create({
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 12,
      height: 52,
      backgroundColor: c.frame,
      borderBottomWidth: 1,
      borderBottomColor: c.border,
    },
    backBtn: {
      width: 34,
      height: 34,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
      alignItems: 'center',
      justifyContent: 'center',
    },
    /** Title over subtitle: the name, then how long it is and what it wears. */
    titleBtn: {flex: 1, minWidth: 0, gap: 1},
    titleTxt: {color: c.text, fontSize: 14, fontWeight: '700'},
    titleSub: {color: c.textMuted, fontSize: 11},
    themePill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
    },
    themeDot: {width: 8, height: 8, borderRadius: 4},
    themeTxt: {fontSize: 12, fontWeight: '700'},
    testerBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: c.borderStrong,
    },
    testerTxt: {color: c.textMuted, fontSize: 12, fontWeight: '600'},
    publishBtn: {
      paddingHorizontal: 14,
      paddingVertical: 7,
      borderRadius: 20,
      backgroundColor: c.accentFill,
    },
    publishTxt: {color: c.onAccent, fontSize: 13, fontWeight: '700'},

    // ── Controls floating over the slide ─────────────────────────────────
    actionRow: {
      bottom: 10,
      left: 10,
      right: 10,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    canvasChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 11,
      paddingVertical: 8,
      borderRadius: 18,
      borderWidth: 1,
      backgroundColor: c.canvas,
      borderColor: c.canvasBorder,
      shadowColor: c.shadow,
      shadowOpacity: 0.3,
      shadowRadius: 8,
      shadowOffset: {width: 0, height: 3},
      elevation: 4,
    },
    canvasChipDanger: {
      backgroundColor: c.canvasDanger,
      borderColor: c.dangerBorder,
    },
    canvasChipTxt: {color: c.onCanvas, fontSize: 12, fontWeight: '700'},

    // ── Sheets (block picker & theme picker) ─────────────────────────────
    pickerSheet: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      maxHeight: '88%',
      borderTopLeftRadius: 22,
      borderTopRightRadius: 22,
      borderTopWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.surface,
      paddingHorizontal: 16,
      paddingTop: 10,
      paddingBottom: 20,
      zIndex: 100,
      overflow: 'hidden',
    },
    themeSheet: {height: '85%', zIndex: 200},
    sheetHandle: {
      width: 38,
      height: 4,
      borderRadius: 2,
      alignSelf: 'center',
      backgroundColor: c.borderStrong,
      marginBottom: 14,
    },
    sheetHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      marginBottom: 12,
    },
    sheetTitle: {color: c.text, fontSize: 17, fontWeight: '800'},
    sheetSub: {color: c.textMuted, fontSize: 11.5, marginTop: 1},
    sheetClose: {
      width: 30,
      height: 30,
      borderRadius: 15,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.fill,
    },
    // ── Slide bar ────────────────────────────────────────────────────────
    pagerBar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 10,
      paddingVertical: 8,
      backgroundColor: c.frame,
      borderTopWidth: 1,
      borderTopColor: c.border,
    },
    pagerChips: {alignItems: 'center', gap: 6, paddingRight: 4},
    pagerChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 7,
      paddingLeft: 5,
      paddingRight: 10,
      paddingVertical: 5,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    pagerChipActive: {
      borderColor: c.accentBorder,
      backgroundColor: c.accentTint,
    },
    pagerChipNum: {
      width: 20,
      height: 20,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.fillStrong,
    },
    pagerChipNumActive: {backgroundColor: c.accentFill},
    pagerChipNumTxt: {color: c.textMuted, fontSize: 11, fontWeight: '800'},
    pagerChipNumTxtActive: {color: c.onAccent},
    pagerChipTxt: {
      color: c.textMuted,
      fontSize: 12,
      fontWeight: '600',
      maxWidth: 96,
    },
    pagerChipTxtActive: {color: c.text},
    pagerAdd: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 12,
      paddingVertical: 7,
      borderRadius: 16,
      backgroundColor: c.accentFill,
    },
    pagerAddTxt: {color: c.onAccent, fontSize: 12, fontWeight: '700'},
  })

/** The studio's styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
